import React, { useEffect, useState } from 'react';
import { Box, Typography, Avatar, Paper, Grid, Divider, Button, IconButton } from '@mui/material';
import PhotoCamera from '@mui/icons-material/PhotoCamera';
import { storageGetCurrentUser, firstLetterUpperCase } from '../../commons/utils';
import { useDispatch } from 'react-redux';
import { getImageStore } from '../../store/files';
import { changeAvatarStore } from '../../store/uploaders';
import { SnackAlert } from '../../components/SnackAlert';

export const Perfil = () => {
    const dispatch = useDispatch();
    const [userInfo, setUserInfo] = useState<any>(null);
    const [logoUrl, setLogoUrl] = useState('');
    const [selectedFile, setSelectedFile] = useState<File | null>(null);

    const [alert_snack, setAlertSnack] = React.useState(false);
    const [msgSnack, setMsgSnack] = React.useState<any>({ msg: '', status: 'success' });

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        const user = await storageGetCurrentUser();
        setUserInfo(user);

        if (user?.photoURL) {
            const response = await dispatch(getImageStore(user.photoURL, 'image-employee'));
            if (response instanceof Blob) {
                setLogoUrl(URL.createObjectURL(response));
            }
        }
    };

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.files && event.target.files.length > 0) {
            const file = event.target.files[0];
            setSelectedFile(file);
            setLogoUrl(URL.createObjectURL(file));
        }
    };

    const handleSavePhoto = async () => {
        if (!selectedFile) return;

        try {
            const res = await dispatch(changeAvatarStore('avatar-employee', selectedFile));

            if (res.ok) {
                setMsgSnack({ msg: 'Foto actualizada correctamente', status: 'success' });
                setAlertSnack(true);
                setSelectedFile(null);
            } else {
                setMsgSnack({ msg: res.msg || 'No se pudo actualizar la foto', status: 'error' });
                setAlertSnack(true);
            }
        } catch (error) {
            setMsgSnack({ msg: 'Ocurrió un error al actualizar la foto', status: 'error' });
            setAlertSnack(true);
        }
    };

    if (!userInfo) return null;

    const companyName = userInfo.cliente?.tradename ? firstLetterUpperCase(userInfo.cliente.tradename) : 'Qi Hotels';

    return (
        <Box sx={{ p: 3, maxWidth: 800, margin: '0 auto' }}>
            <SnackAlert open={alert_snack} onclick={setAlertSnack} text={msgSnack.msg} severity={msgSnack.status}/>
            <Typography variant="h4" sx={{ mb: 3, fontWeight: 'bold' }}>
                Mi Perfil
            </Typography>

            <Paper sx={{ p: 4, borderRadius: 2, boxShadow: 3 }}>
                <Grid container spacing={4}>
                    {/* Sección de Foto */}
                    <Grid size={{ xs: 12, md: 4 }} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <Box sx={{ position: 'relative' }}>
                            <Avatar 
                                src={logoUrl} 
                                sx={{ width: 150, height: 150, mb: 2, boxShadow: 2 }}
                            />
                            <input
                                accept="image/*"
                                style={{ display: 'none' }}
                                id="icon-button-file"
                                type="file"
                                onChange={handleFileChange}
                            />
                            <label htmlFor="icon-button-file">
                                <IconButton 
                                    color="primary" 
                                    aria-label="upload picture" 
                                    component="span"
                                    sx={{ 
                                        position: 'absolute', 
                                        bottom: 16, 
                                        right: 0, 
                                        backgroundColor: 'white',
                                        '&:hover': { backgroundColor: '#f0f0f0' }
                                    }}
                                >
                                    <PhotoCamera />
                                </IconButton>
                            </label>
                        </Box>
                        
                        {selectedFile && (
                            <Button 
                                variant="contained" 
                                color="primary" 
                                sx={{ mt: 2 }}
                                onClick={handleSavePhoto}
                            >
                                Guardar Foto
                            </Button>
                        )}
                        <Typography variant="caption" color="textSecondary" sx={{ mt: 1, textAlign: 'center', display: 'block' }}>
                            Haz clic en el icono de cámara para cambiar tu foto.
                        </Typography>
                    </Grid>

                    {/* Sección de Datos */}
                    <Grid  size={{ xs: 12, md: 8 }}>
                        <Box sx={{ mb: 3 }}>
                            <Typography variant="h5" sx={{ fontWeight: 'medium', mb: 1 }}>
                                Datos Personales
                            </Typography>
                            <Divider sx={{ mb: 2 }} />
                            
                            <Grid container spacing={2}>
                                <Grid size={{ xs: 12, sm: 6 }}>
                                    <Typography variant="subtitle2" color="textSecondary">
                                        Nombre Completo
                                    </Typography>
                                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                        {userInfo.fullName || userInfo.displayName || userInfo.name || 'N/A'}
                                    </Typography>
                                </Grid>
                                
                                <Grid size={{ xs: 12, sm: 6 }}>
                                    <Typography variant="subtitle2" color="textSecondary">
                                        Correo Electrónico
                                    </Typography>
                                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                        {userInfo.email || 'N/A'}
                                    </Typography>
                                </Grid>

                                <Grid size={{ xs: 12, sm: 6 }}>
                                    <Typography variant="subtitle2" color="textSecondary">
                                        Empresa
                                    </Typography>
                                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                        {companyName}
                                    </Typography>
                                </Grid>

                                <Grid size={{ xs: 12, sm: 6 }}>
                                    <Typography variant="subtitle2" color="textSecondary">
                                        Rol / Cargo
                                    </Typography>
                                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                        {userInfo.cargo || userInfo.cargo || 'N/A'}
                                    </Typography>
                                </Grid>

                                <Grid size={{ xs: 12, sm: 6 }}>
                                    <Typography variant="subtitle2" color="textSecondary">
                                        Estado de Cuenta
                                    </Typography>
                                    <Typography variant="body1" sx={{ fontWeight: 500, color:'success.main'  }}>
                                        Activo
                                    </Typography>
                                </Grid>
                            </Grid>
                        </Box>
                    </Grid>
                </Grid>
            </Paper>
        </Box>
    );
};
